import { Controller } from "@hotwired/stimulus"
import { Collapse } from "bootstrap"
import { create, RangePlugin } from "@easepick/bundle"

// Connects to data-controller="retreat-filter"
export default class extends Controller {
  static targets = ["frame", "dateRangeTrigger", "dateFrom", "dateTo", "resetLink"]
  static values  = {
    baseUrl: String,
    debounce: { type: Number, default: 300 }
  }

  initialize() {
    this._timeout = null
  }

  connect() {
    if (!this.hasDateRangeTriggerTarget) return

    // `create` is the easepick `Core` class (re-exported under the name
    // `create`), not a factory function — it must be instantiated with `new`.
    this._picker = new create({
      element: this.dateRangeTriggerTarget,
      lang: "de-DE",
      format: "DD.MM.YYYY",
      css: [
        "https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.1/dist/index.css"
      ],
      zIndex: 10,
      grid: 2,
      calendars: 2,
      plugins: [RangePlugin],
      autoApply: false,
      locale: {
        cancel: "Abbrechen",
        apply: "Auswählen"
      },
      RangePlugin: {
        startDate: this.dateFromTarget.value ? new Date(this.dateFromTarget.value) : undefined,
        endDate: this.dateToTarget.value ? new Date(this.dateToTarget.value) : undefined
      },
      setup: (picker) => {
        picker.on("select", (e) => {
          const { start, end } = e.detail
          this.dateFromTarget.value = start.format("YYYY-MM-DD")
          this.dateToTarget.value = end.format("YYYY-MM-DD")
          this.filter()
        })
      }
    })
  }

  disconnect() {
    if (this._picker) {
      this._picker.destroy()
      this._picker = null
    }
  }

  filter() {
    clearTimeout(this._timeout)
    this._timeout = setTimeout(() => this._applyFilter(), this.debounceValue)
  }

  toggleParent(event) {
    const checkbox = event.currentTarget
    const selector = checkbox.dataset.retreatFilterChildrenParam
    const children = this.element.querySelectorAll(`${selector} input[type="checkbox"]`)

    children.forEach(child => child.checked = checkbox.checked)

    if (checkbox.checked) {
      const collapseEl = this.element.querySelector(checkbox.dataset.retreatFilterCollapseParam)
      if (collapseEl && !collapseEl.classList.contains("show")) {
        new Collapse(collapseEl, { toggle: true })
      }
    }

    this.filter()
  }

  toggleChild(event) {
    const checkbox = event.currentTarget
    const parentId = checkbox.dataset.retreatFilterParentParam
    const parentCheckbox = this.element.querySelector(`#${parentId}`)

    if (parentCheckbox) {
      const selector = parentCheckbox.dataset.retreatFilterChildrenParam
      const children = Array.from(this.element.querySelectorAll(`${selector} input[type="checkbox"]`))
      const checkedCount = children.filter(c => c.checked).length

      if (checkedCount === 0) {
        parentCheckbox.checked = false
        parentCheckbox.indeterminate = false
      } else if (checkedCount === children.length) {
        parentCheckbox.checked = true
        parentCheckbox.indeterminate = false
      } else {
        parentCheckbox.checked = false
        parentCheckbox.indeterminate = true
      }
    }

    this.filter()
  }

  reset(event) {
    event.preventDefault()
    this.element.querySelectorAll('input[type="checkbox"]').forEach(cb => {
      cb.checked = false
      cb.indeterminate = false
    })
    this.dateFromTarget.value = ""
    this.dateToTarget.value = ""
    if (this._picker) this._picker.clear()
    this._applyFilter()
  }

  _applyFilter() {
    const params = new URLSearchParams()
    const checkboxes = this.element.querySelectorAll('input[type="checkbox"][name]:checked')

    checkboxes.forEach(cb => {
      params.append(cb.name, cb.value)
    })

    if (this.dateFromTarget.value) params.append("date_from", this.dateFromTarget.value)
    if (this.dateToTarget.value)   params.append("date_to", this.dateToTarget.value)

    const hasActiveFilters = params.toString() !== ""
    if (this.hasResetLinkTarget) this.resetLinkTarget.classList.toggle("d-none", !hasActiveFilters)

    // Sentinel: marks this request as an explicit user action (even if the resulting
    // selection is empty), so the server can distinguish "user cleared everything" from
    // "first visit, apply nav-context preselection" (see filter_params_present? in
    // retreats_controller.rb).
    params.append("filtered", "1")

    const queryString = params.toString()
    const url = queryString ? `${this.baseUrlValue}?${queryString}` : this.baseUrlValue

    this.frameTarget.src = url
    // Turbo-State (restorationIdentifier) beibehalten, sonst ignoriert Turbo
    // später den Zurück-Button für diesen History-Eintrag.
    history.replaceState(history.state, "", url)
  }
}
